import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  ArchiveBoxIcon, 
  EyeIcon, 
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const Inventory = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showLowStockModal, setShowLowStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [stockData, setStockData] = useState({
    product_id: '',
    depot_id: '',
    // batch_number is removed - will be auto-generated by backend
    supplier: '',
    purchase_date: format(new Date(), 'yyyy-MM-dd'),
    initial_quantity_cases: '',
    cost_per_case: '',
    price_per_case: '',
    expiry_date: ''
  });
  const [productData, setProductData] = useState({
    name: '',
    brand: '',
    category: '',
    manufacturer: '',
    bottles_per_case: 12,
    unit_volume_ml: '',
    description: ''
  });
  const [filters, setFilters] = useState({
    category: '',
    low_stock: false,
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });

  useEffect(() => {
    fetchInventory();
    fetchProducts();
    fetchDepots();
    checkLowStock();
  }, [pagination.page, pagination.per_page, filters]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/reports/inventory', {
        params: {
          page: pagination.page,
          per_page: pagination.per_page,
          category: filters.category,
          low_stock: filters.low_stock,
          search: filters.search
        }
      });
      console.log("response.data.data in Inventory.js = ", response.data);
      setInventory(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || response.data.data?.length || 0
      }));
    } catch (error) {
      toast.error('Failed to fetch inventory');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/', {
        params: { per_page: 1000 }
      });
      console.log("response.data.items under products in Inventory.js = ", response.data);
      setProducts(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await api.get('/depot/');
      console.log("response.data.items under depots in Inventory.js = ", response.data);
      setDepots(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch depots:', error);
    }
  };

  const fetchProductBatches = async (productId) => {
    try {
      const response = await api.get(`/products/${productId}/batches`);
      setBatches(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const checkLowStock = async () => {
    try {
      const response = await api.get('/reports/inventory/low-stock');
      console.log("response.data.items under low stock in Inventory.js = ", response.data);
      setLowStockItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to check low stock:', error);
    }
  };

  const handleAddStock = async () => {
    try {
      // Remove any batch_number from the data if present - let backend generate it
      const submissionData = { ...stockData };
      delete submissionData.batch_number; // Ensure batch_number is not sent
      
      await api.post(`/products/${stockData.product_id}/batches`, submissionData);
      toast.success('Stock added successfully with auto-generated batch number');
      setShowAddStockModal(false);
      resetStockForm();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add stock');
    }
  };

  const handleCreateProduct = async () => {
    try {
      await api.post('/products/', productData);
      toast.success('Product created successfully');
      setShowProductModal(false);
      resetProductForm();
      fetchProducts();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create product');
    }
  };

  const handleViewProduct = (product) => {
    navigate(`/inventory/product/${product.Product_id}`);
  };

  const handleViewBatches = async (product) => {
    setSelectedProduct(product);
    await fetchProductBatches(product.Product_id);
    setShowLowStockModal(true);
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get('/reports/inventory', {
        params: { format },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Inventory exported successfully');
    } catch (error) {
      toast.error('Failed to export inventory');
    }
  };

  const resetStockForm = () => {
    setStockData({
      product_id: '',
      depot_id: '',
      // batch_number is removed
      supplier: '',
      purchase_date: format(new Date(), 'yyyy-MM-dd'),
      initial_quantity_cases: '',
      cost_per_case: '',
      price_per_case: '',
      expiry_date: ''
    });
  };

  const resetProductForm = () => {
    setProductData({
      name: '',
      brand: '',
      category: '',
      manufacturer: '',
      bottles_per_case: 12,
      unit_volume_ml: '',
      description: ''
    });
    setSelectedProduct(null);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      low_stock: false,
      search: ''
    });
  };

  const getTotalValue = () => {
    return inventory.reduce((sum, item) => sum + (item['Inventory Value'] || 0), 0);
  };

  const getTotalCases = () => {
    return inventory.reduce((sum, item) => sum + (item['Total Cases (All)'] || 0), 0);
  };

  const getUniqueCategories = () => {
    const categories = inventory.map(item => item.Category).filter(Boolean);
    return [...new Set(categories)];
  };

  const columns = [
    {
      header: 'Product',
      accessorKey: 'Product',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.Product}</div>
          <div className="text-xs text-gray-500">{row.original.Brand}</div>
        </div>
      )
    },
    {
      header: 'Category',
      accessorKey: 'Category',
      cell: ({ getValue }) => getValue() || '-'
    },
    {
      header: 'Initial Stock',
      accessorKey: 'Cases in Depot',
      cell: ({ row }) => (
        <div>
          <div>{row.original['Cases in Depot']} cases</div>
          <div className="text-xs text-gray-500">{row.original['Bottles in Depot']} bottles</div>
        </div>
      )
    },
    {
      header: 'Current Stock',
      accessorKey: 'Total Cases (All)',
      cell: ({ getValue, row }) => (
        <div>
          <div>{getValue()} cases</div>
          <div className="text-xs text-gray-500">{row.original['Current Bottles in Depot']} bottles</div>
        </div>
      )
    },
    {
      header: 'Avg Cost',
      accessorKey: 'Avg Cost',
      cell: ({ getValue }) => `ZMW ${getValue()?.toFixed(2) || '0.00'}`
    },
    {
      header: 'Avg Price',
      accessorKey: 'Avg Price',
      cell: ({ getValue }) => `ZMW ${getValue()?.toFixed(2) || '0.00'}`
    },
    {
      header: 'Total Value',
      accessorKey: 'Inventory Value',
      cell: ({ getValue }) => (
        <span className="font-medium text-primary-600">
          ZMW {getValue()?.toLocaleString() || 0}
        </span>
      )
    },
    {
      header: 'Status',
      cell: ({ row }) => {
        const cases = row.original['Total Cases (All)'] || 0;
        if (cases < 10) return <StatusBadge status="low_stock" />;
        if (cases < 20) return <StatusBadge status="medium_stock" />;
        return <StatusBadge status="in_stock" />;
      }
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewProduct(row.original)}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleViewBatches(row.original)}
            className="text-green-600 hover:text-green-900"
            title="View Batches"
          >
            <ArchiveBoxIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Inventory Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Track and manage product inventory across all depots.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          <button
            type="button"
            onClick={() => handleExport('excel')}
            className="btn-secondary"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export
          </button>
          <button
            type="button"
            onClick={() => {
              resetProductForm();
              setShowProductModal(true);
            }}
            className="btn-secondary"
          >
            <ArchiveBoxIcon className="h-4 w-4 mr-2" />
            New Product
          </button>
          <button
            type="button"
            onClick={() => {
              resetStockForm();
              setShowAddStockModal(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Stock
          </button>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <span className="font-medium">Low Stock Alert!</span> {lowStockItems.length} products are running low on stock.
                <button
                  onClick={() => setShowLowStockModal(true)}
                  className="ml-2 font-medium underline text-yellow-700 hover:text-yellow-600"
                >
                  View Details
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-500 rounded-md p-3">
                <ArchiveBoxIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Products</dt>
                  <dd className="text-lg font-medium text-gray-900">{products.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <ArchiveBoxIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Cases</dt>
                  <dd className="text-lg font-medium text-gray-900">{getTotalCases()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Value</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ZMW {getTotalValue().toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Search</label>
            <input
              type="text"
              placeholder="Search products..."
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              {getUniqueCategories().map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={filters.low_stock}
                onChange={(e) => handleFilterChange('low_stock', e.target.checked)}
              />
              <span className="ml-2 text-sm text-gray-700">Show low stock only</span>
            </label>
          </div>
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="mt-8">
        <DataTable
          data={inventory}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* Add Stock Modal - Batch number field removed */}
      <Modal
        isOpen={showAddStockModal}
        onClose={() => {
          setShowAddStockModal(false);
          resetStockForm();
        }}
        title="Add Stock to Inventory"
        size="lg"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleAddStock();
        }}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Product *</label>
                <select
                  required
                  className="form-input"
                  value={stockData.product_id}
                  onChange={(e) => setStockData({...stockData, product_id: e.target.value})}
                >
                  <option value="">Select Product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.bottles_per_case} bottles/case)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Depot *</label>
                <select
                  required
                  className="form-input"
                  value={stockData.depot_id}
                  onChange={(e) => setStockData({...stockData, depot_id: e.target.value})}
                >
                  <option value="">Select Depot</option>
                  {depots.map(depot => (
                    <option key={depot.id} value={depot.id}>{depot.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Supplier</label>
                <input
                  type="text"
                  className="form-input"
                  value={stockData.supplier}
                  onChange={(e) => setStockData({...stockData, supplier: e.target.value})}
                  placeholder="Enter supplier name"
                />
              </div>

              <div>
                <label className="form-label">Purchase Date *</label>
                <input
                  type="date"
                  required
                  className="form-input"
                  value={stockData.purchase_date}
                  onChange={(e) => setStockData({...stockData, purchase_date: e.target.value})}
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Expiry Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={stockData.expiry_date}
                  onChange={(e) => setStockData({...stockData, expiry_date: e.target.value})}
                  min={stockData.purchase_date}
                />
              </div>

              <div>
                <label className="form-label">Quantity (Cases) *</label>
                <input
                  type="number"
                  min="1"
                  required
                  className="form-input"
                  value={stockData.initial_quantity_cases}
                  onChange={(e) => setStockData({...stockData, initial_quantity_cases: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Cost per Case (ZMW) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  className="form-input"
                  value={stockData.cost_per_case}
                  onChange={(e) => setStockData({...stockData, cost_per_case: parseFloat(e.target.value)})}
                />
              </div>

              <div>
                <label className="form-label">Selling Price per Case (ZMW) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  className="form-input"
                  value={stockData.price_per_case}
                  onChange={(e) => setStockData({...stockData, price_per_case: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            {/* Info message about auto-generated batch number */}
            {/*
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
              
                <span className="font-medium">Note:</span> A unique batch number will be automatically generated by the system for this stock entry.
              

              </p>
            </div>
            */}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddStockModal(false);
                  resetStockForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Add Stock
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Create Product Modal */}
      <Modal
        isOpen={showProductModal}
        onClose={() => {
          setShowProductModal(false);
          resetProductForm();
        }}
        title="Create New Product"
        size="lg"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleCreateProduct();
        }}>
          <div className="space-y-4">
            <div>
              <label className="form-label">Product Name *</label>
              <input
                type="text"
                required
                className="form-input"
                value={productData.name}
                onChange={(e) => setProductData({...productData, name: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Brand</label>
                <input
                  type="text"
                  className="form-input"
                  value={productData.brand}
                  onChange={(e) => setProductData({...productData, brand: e.target.value})}
                />
              </div>

              <div>
                <label className="form-label">Category</label>
                <input
                  type="text"
                  className="form-input"
                  value={productData.category}
                  onChange={(e) => setProductData({...productData, category: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Manufacturer</label>
              <input
                type="text"
                className="form-input"
                value={productData.manufacturer}
                onChange={(e) => setProductData({...productData, manufacturer: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Bottles per Case *</label>
                <input
                  type="number"
                  min="1"
                  required
                  className="form-input"
                  value={productData.bottles_per_case}
                  onChange={(e) => setProductData({...productData, bottles_per_case: parseInt(e.target.value)})}
                />
              </div>

              <div>
                <label className="form-label">Volume per Bottle (ml)</label>
                <input
                  type="number"
                  min="1"
                  className="form-input"
                  value={productData.unit_volume_ml}
                  onChange={(e) => setProductData({...productData, unit_volume_ml: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                rows="3"
                value={productData.description}
                onChange={(e) => setProductData({...productData, description: e.target.value})}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowProductModal(false);
                  resetProductForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Create Product
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Low Stock / Batches Modal */}
      <Modal
        isOpen={showLowStockModal}
        onClose={() => {
          setShowLowStockModal(false);
          setBatches([]);
        }}
        title={selectedProduct ? `${selectedProduct.Product} - Batches` : 'Low Stock Items'}
        size="lg"
      >
        {selectedProduct ? (
          <div>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Total Stock: <span className="font-medium">{selectedProduct['Total Cases (All)']} cases</span>
              </p>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purchase Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batches.map(batch => (
                  <tr key={batch.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{batch.batch_number || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{batch.supplier || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {batch.purchase_date ? format(new Date(batch.purchase_date), 'dd MMM yyyy') : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{batch.current_quantity_cases} cases</td>
                    <td className="px-6 py-4 text-sm text-gray-500">ZMW {batch.cost_per_case.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">ZMW {batch.price_per_case.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {batch.expiry_date ? format(new Date(batch.expiry_date), 'dd MMM yyyy') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>
            <p className="mb-4 text-sm text-gray-600">
              The following products are running low on stock:
            </p>
            <div className="space-y-2">
              {lowStockItems.map(item => (
                <div key={item.id} className="flex justify-between items-center p-3 bg-yellow-50 rounded">
                  <span className="font-medium">{item.name}</span>
                  <span className="text-sm text-gray-600">{item.current_stock} cases remaining</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Inventory;